import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectProductSourceDownloads, selectSourceDownloadURL } from './duck/selectors';
import { SectionMessage } from 'components/common/GenericErrorMessage';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { messages } from './messages';
import MACPageHeader from './components/macPageHeader';
import config from 'appconfig';
import t from 'modules/i18n/intl';
import {
  PageH3Header,
  StyledTextParapgraph,
  StyledRedSpan,
  StyledTableRow,
  StyledSpan,
} from './components/styledComponents';
import { fetchProductSourceDownload, fetchSourceDownloadURL } from './duck/actions/downloads';
import ButtonLink from 'components/common/ButtonLink';
import { downloadByLinkCreation } from 'util/mac/downloadByLinkCreation';
import { State } from 'model/State';

const contactUs = (chunks: string): React.ReactNode => <a href={config.companyContact}>{chunks}</a>;

const atlassianAnswersLink = (chunks: string): React.ReactNode => (
  <a href="https://answers.atlassian.com/">{chunks}</a>
);

export const askUsLink = (chunks: string): React.ReactNode => (
  <a
    href="https://www.atlassian.com/company/contact/purchasing-licensing#/"
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </a>
);

type SourceDownloads = {
  id: string;
  name: string;
  size: string;
  dateAdded: string;
  tarURL: string | null;
  zipURL: string | null;
};

export const DownloadSourcePage = () => {
  const dispatch = useDispatch();
  const productKey = useSelector((state: State) => state.location.payload.productKey);
  const productSourceDownloadsResult: any = useSelector(selectProductSourceDownloads);
  const sourceDownloadsURLResult: { _result?: string } = useSelector(selectSourceDownloadURL);
  const [productSourceDownloads, setProductSourceDownloads] = useState([]);

  useEffect(() => {
    dispatch(fetchProductSourceDownload(productKey));
  }, []);

  useEffect(() => {
    if (productSourceDownloadsResult?._result) {
      setProductSourceDownloads(productSourceDownloadsResult._result);
    }
  }, [productSourceDownloadsResult]);

  useEffect(() => {
    if (sourceDownloadsURLResult?._result) {
      downloadByLinkCreation(sourceDownloadsURLResult._result, '');
    }
  }, [sourceDownloadsURLResult._result]);

  const isNonAtlassianUser =
    productSourceDownloadsResult.error?.errorDetail ===
    'Only Atlassians can access source downloads';

  return (
    <>
      <MACPageHeader />

      {isNonAtlassianUser && (
        <SectionMessage appearance="error">
          {t(messages.downloadSourceNonAtlassianUserError, { contactLink: contactUs })}
        </SectionMessage>
      )}

      <PageH3Header>
        <FormattedMessage {...messages.downloadSourcePageTitle} />
      </PageH3Header>

      <StyledTextParapgraph>
        <FormattedMessage
          {...messages.downloadSourceLicensePeriod}
          values={{ product: productSourceDownloads[0]?.version?.product?.name }}
        />
        <StyledRedSpan>
          <FormattedMessage {...messages.downloadSourceDoNotDistribute} />
        </StyledRedSpan>
      </StyledTextParapgraph>
      <StyledTextParapgraph>
        {t(messages.downloadSourceBetaDeveloperReleasesText, {
          product1: productSourceDownloads[0]?.version?.product?.name,
          product2: productSourceDownloads[0]?.version?.product?.name,
          contactUs,
        })}
      </StyledTextParapgraph>
      <StyledTextParapgraph>
        {t(messages.downloadSourceQuestionsInfo, {
          atlassianAnswersLink,
          askUsLink,
        })}
      </StyledTextParapgraph>
      <br />
      <table data-cy="source-downloads-table">
        <thead>
          <tr>
            <th>
              <FormattedMessage {...messages.downloadSourceVersion} />
            </th>
            <th>
              <FormattedMessage {...messages.downloadSourceSize} />
            </th>
            <th>
              <FormattedMessage {...messages.downloadSourceAdded} />
            </th>
            <th>
              <StyledSpan $paddingLeft="10px">
                <FormattedMessage {...messages.downloadSourceFormat} />
              </StyledSpan>
            </th>
          </tr>
        </thead>
        <tbody>
          {isNonAtlassianUser || productSourceDownloads.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <p>{t(messages.noSourceDownloads, { contactLink: contactUs })}</p>
              </td>
            </tr>
          ) : (
            productSourceDownloads.map(
              ({ id, name, size, tarURL, zipURL, dateAdded }: SourceDownloads) => {
                const isLinkAvailable = Boolean(name.includes('TAR') ? tarURL : zipURL);
                return (
                  isLinkAvailable && (
                    <StyledTableRow key={id} data-cy={`source-download-row-${id}`}>
                      <td>
                        <p>{name}</p>
                      </td>
                      <td>
                        <p>{size}</p>
                      </td>
                      <td>
                        <FormattedDate
                          value={new Date(dateAdded)}
                          day="2-digit"
                          month="short"
                          year="numeric"
                        />
                      </td>
                      <td>
                        <ButtonLink
                          onClick={() =>
                            dispatch(
                              fetchSourceDownloadURL(id, name.includes('TAR') ? 'tar' : 'zip'),
                            )
                          }
                        >
                          {name.includes('TAR') ? 'TAR.GZ' : 'ZIP'}
                        </ButtonLink>
                      </td>
                    </StyledTableRow>
                  )
                );
              },
            )
          )}
        </tbody>
      </table>
    </>
  );
};
